import React from 'react'
import { Container, Content } from './styles'
import { QRCodeSVG } from 'qrcode.react'
import ModalDelete from '../../../../components/Modals/ModalDelete'
import { SideBarStyles } from '../../Logs/SideBarLogs/styles'
import ClickAwayListener from 'react-click-away-listener'
import { useForm } from 'react-hook-form'
import Input from '../../../../components/Input'
import { format } from 'date-fns'

import GroupsSearch from './GroupsSearch'
import { Document } from '../../../../services/documents.service'
import { api } from '../../../../services/api'
import { formatFileSize } from '../../../../utils/utils'
import useToast from '../../../../hooks/useToast'
import { getFavorites } from '../../../../services/utils'
import { useUserStore } from '../../../../store/user'
import { usePermissions } from '../../../../hooks/usePermissions'
import {
  FaCalendar,
  FaCheck,
  FaHeart,
  FaPencil,
  FaRegHeart,
  FaTrash,
  FaXmark,
} from 'react-icons/fa6'
import { ButtonIcon } from '../../../../components/Table/Pagination/styles'
import { useTheme } from 'styled-components'
import { SelectDocumentType } from '../../../../components/Modals/ModalUpload'
import { Content as ContentInput, Field, Label } from '../../../../components/Input/styles'
import { ButtonRound } from '../../../../components/Header/styles'
import { ResponseError } from '../../../../validators/utils.interface'
import axios from 'axios'

export type ModalDeleteRef = React.ElementRef<typeof ModalDelete>

export interface FileDetailsProps {
  refetch?: () => void
}

export interface ModalHandle {
  openModal: (_id: string) => void
  closeModal: () => void
}

const FileDetails: React.ForwardRefRenderFunction<ModalHandle, FileDetailsProps> = (
  { refetch },
  ref,
) => {
  const [open, setOpen] = React.useState(false)
  const { user } = useUserStore()
  const { canEditDocuments, canDeleteDocuments } = usePermissions()

  // Verificar permissões baseadas em grupo ao invés de role do usuário
  const canEdit = canEditDocuments
  const canDelete = canDeleteDocuments
  const [isLoading, setIsLoading] = React.useState(false)
  const [fileDetails, setFileDetails] = React.useState<Document>()
  const [openTimeout, setOpenTimeout] = React.useState(false)

  const [edit, setEdit] = React.useState(false)
  const [metadata, setMetadata] = React.useState([])

  const toast = useToast()
  const theme: any = useTheme()

  const modalDeleteRef = React.useRef<ModalDeleteRef>(null)

  const {
    control,
    handleSubmit,
    setValue,
    setFocus,
    watch,
    getValues,
    formState: { errors },
  } = useForm<Document>({
    defaultValues: {
      name: '',
      createdAt: '',
      size: '',
      owner: '',
      documentType: '',
      isFolder: false
    }
  })

  const handleGetData = async (_id?: string, refreshSim = false) => {
    try {
      setIsLoading(true)
      const { data: response } = await api.get(`/documents/${_id}${refreshSim ? '?refreshSim=true' : ''}`)

      // Os dados do documento estão em response.document, não diretamente em response
      let documentData: any = response.document || response

      setFileDetails(documentData)
      
      // Debug: verificar estrutura do owner
      console.log('Dados do documento recebidos:', {
        owner: documentData?.owner,
        ownerType: typeof documentData?.owner,
        ownerName: documentData?.owner?.name,
        fullDocument: documentData
      })
      
      setValue('documentType', documentData?.documentTypeWithData?.documentType?.name || '')
      if (documentData?.documentTypeWithData) {
        let docMetadata = documentData.documentTypeWithData.metadata || [];
        const schemaMetadata = documentData.documentTypeWithData.documentType?.metadata || [];
        
        if (docMetadata.length === 0 && schemaMetadata.length > 0) {
           docMetadata = schemaMetadata.map((m: any) => ({ name: m.name, value: 'Não informado' }));
        }
        setMetadata(docMetadata)
      }
      setValue('name', documentData.name || '')
      
      // Verificar se a data é válida antes de formatar
      if (documentData.createdAt) {
        try {
          const date = new Date(documentData.createdAt)
          if (!isNaN(date.getTime())) {
            setValue('createdAt', format(date, 'dd/MM/yyyy HH:mm'))
          } else {
            setValue('createdAt', 'Data inválida')
          }
        } catch (error) {
          console.error('Erro ao formatar data:', error)
          setValue('createdAt', 'Data inválida')
        }
      } else {
        setValue('createdAt', 'Data não disponível')
      }
      
      setValue('isFolder', documentData.isFolder)
      if (!documentData.isFolder && documentData.size) {
        let size = formatFileSize(Number(documentData.size))
        setValue('size', size)
      }
      
      // Tratar owner: pode vir como objeto {name: "..."}, string (ID) ou null
      let ownerName = ''
      if (documentData?.owner) {
        if (typeof documentData.owner === 'object' && documentData.owner.name) {
          // Owner está populado como objeto
          ownerName = documentData.owner.name
        } else if (typeof documentData.owner === 'string') {
          // Owner é apenas o ID, não foi populado - deixar vazio ou buscar
          ownerName = ''
          console.warn('Owner retornado como ID string, não foi populado:', documentData.owner)
        }
      }
      setValue('owner', ownerName)
      
      // Log para verificar se os valores foram definidos
      setTimeout(() => {
        console.log('Valores definidos no formulário:', {
          name: getValues('name'),
          documentType: getValues('documentType'),
          owner: getValues('owner'),
          createdAt: getValues('createdAt'),
          size: getValues('size'),
          isFolder: getValues('isFolder')
        })
        setOpenTimeout(true)
      }, 500)
    } catch (error) {
      console.error('Erro ao carregar dados do documento:', error)
      toast.errorToast('Erro ao carregar informações do documento')
    } finally {
      setIsLoading(false)
    }
  }

  const onEditName = async () => {
    setEdit(true)

    setFocus('name')
  }

  const handleEditName = async () => {
    setEdit(true)

    const name = getValues('name')
    handleEdit({
      name,
    })
    setEdit(false)
  }

  const handleSubmitDelete = async (_id: string) => {
    try {
      await api.delete(`/documents/${_id}`)

      toast.successToast('Arquivo deletado com sucesso!')
      if (refetch) refetch()
      handleClickAway()
    } catch (error) {
      const typedError = error as ResponseError
      if (axios.isAxiosError(error)) {
        toast.errorToast(typedError?.response?.data?.message)
      } else {
        toast.errorToast('Erro ao deletar o registro !')
      }
    } finally {
      modalDeleteRef.current?.closeModal()
    }
  }

  const handleEdit = async (formData: any) => {
    try {
      await api.put(`/documents/${fileDetails?._id}`, formData)

      toast.successToast('Arquivo editado com sucesso!')
      handleGetData(fileDetails?._id)
      if (refetch) refetch()
      getFavorites()
    } catch (error) {}
  }

  const handleClickAway = () => {
    setOpen(false)
    setOpenTimeout(false)
    setFileDetails(undefined)
  }

  React.useImperativeHandle(ref, () => ({
    openModal(_id) {
      setOpen(true)
      setMetadata([])
      handleGetData(_id)
    },
    closeModal() {
      setOpen(false)
    },
  }))

  const name = watch('name')

  return (
    <Container open={open}>
      <Content>
        <SideBarStyles.Header>
          <SideBarStyles.Title>{fileDetails?.name}</SideBarStyles.Title>
          <ButtonIcon onClick={() => handleClickAway()}>
            <FaXmark size={25} />
          </ButtonIcon>
        </SideBarStyles.Header>
        <SideBarStyles.ActionsContent>
          {fileDetails?.isFavorite ? (
            <FaHeart
              onClick={() =>
                handleEdit({
                  isFavorite: false,
                })
              }
              size={22}
              color={theme.colors.primary}
            />
          ) : (
            <FaRegHeart
              onClick={() =>
                handleEdit({
                  isFavorite: true,
                })
              }
              size={22}
              color={theme.colors.primary}
            />
          )}
          {canDelete && (
            <FaTrash
              color={theme.colors.primary}
              onClick={() => fileDetails?._id && modalDeleteRef.current?.openModal(fileDetails?._id)}
              size={22}
            />
          )}
        </SideBarStyles.ActionsContent>
        <Input
          small
          label='Nome'
          error={errors?.name}
          control={control}
          type='text'
          name='name'
          Icon={
            canEdit && edit ? (
              <ButtonRound
                style={{
                  backgroundColor: theme.colors.success,
                  minWidth: 25,
                  minHeight: 25,
                  width: 25,
                  height: 25,
                }}
                onClick={handleEditName}
              >
                <FaCheck color='white' size={15} />
              </ButtonRound>
            ) : canEdit ? (
              <ButtonRound
                style={{
                  minWidth: 25,
                  minHeight: 25,
                  width: 25,
                  height: 25,
                }}
                onClick={onEditName}
              >
                <FaPencil color='white' size={13} />
              </ButtonRound>
            ) : null
          }
        />
        <Input
          label='Data'
          error={errors?.createdAt}
          control={control}
          disabled
          small
          Icon={<FaCalendar size={20} />}
          type='text'
          name='createdAt'
        />

        {!fileDetails?.isFolder && (
          <Input
            label='Tamanho do arquivo'
            error={errors?.size}
            control={control}
            disabled
            small
            type='text'
            name='size'
          />
        )}

        <Input label='Quem criou' small control={control} disabled type='text' name='owner' />

        <Input 
          label='Tipo de Documento' 
          small 
          control={control} 
          disabled 
          type='text' 
          name='documentType'
        />

        {fileDetails?.simDataFound && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#ecfdf5',
              border: '1px solid #a7f3d0',
              padding: '7px 12px',
              borderRadius: '6px',
              margin: '10px 0 6px 0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#065f46', fontSize: '12px', fontWeight: 700 }}>
              <FaCheck size={13} color="#10b981" /> Dados vinculados ao SIM (TCE-CE)
            </div>
            <button
              type="button"
              onClick={() => handleGetData(fileDetails._id, true)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#059669',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Recarregar SIM
            </button>
          </div>
        )}

        {!fileDetails?.simDataFound && fileDetails?._id && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '8px 0 4px 0' }}>
            <button
              type="button"
              onClick={() => handleGetData(fileDetails._id, true)}
              style={{
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '4px',
                padding: '4px 8px',
                color: '#334155',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              🔄 Buscar no SIM (TCE-CE)
            </button>
          </div>
        )}

        {metadata.map((meta: any, index: number) => {
          const isLongText = meta?.name?.toLowerCase().includes('objeto') || (meta?.value && String(meta.value).length > 60);
          return (
            <div key={index} style={{ marginBottom: '8px' }}>
              <Label>{meta?.name}</Label>
              <ContentInput gray style={{ height: isLongText ? 'auto' : undefined, minHeight: isLongText ? '60px' : undefined, padding: isLongText ? '6px' : undefined }}>
                {isLongText ? (
                  <textarea
                    disabled
                    value={meta.value}
                    style={{
                      width: '100%',
                      border: 'none',
                      background: 'transparent',
                      resize: 'none',
                      fontSize: '13px',
                      color: theme.colors.input,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      minHeight: '55px',
                    }}
                  />
                ) : (
                  <Field error={false} name={`meta_${index}`} small disabled value={meta.value} />
                )}
              </ContentInput>
            </div>
          );
        })}

        <div
          style={{
            borderBottom: '2px solid #64646420',
          }}
        />

        {fileDetails?._id && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '20px', marginBottom: '20px', gap: '10px' }}>
            <Label style={{ fontSize: '15px' }}>Etiqueta Física (QR Code)</Label>
            <ButtonIcon 
              onClick={() => window.open(`/print/qrcode/${fileDetails._id}`, '_blank')}
              style={{ width: '100%', padding: '12px', background: theme.colors.primary, color: 'white', borderRadius: '8px', fontSize: '14px', marginTop: '5px' }}
            >
              Imprimir Etiqueta
            </ButtonIcon>
          </div>
        )}

        <div
          style={{
            borderBottom: '2px solid #64646420',
          }}
        />
        {fileDetails?._id && (
          <GroupsSearch
            menuPosition={'absolute'}
            menuPortalTarget={document.querySelector('#portalDocumentDetails') || document.body}
            fileId={[fileDetails?._id]}
          />
        )}
        <ModalDelete
          loading={isLoading}
          title='Deletar Arquivo?'
          ref={modalDeleteRef}
          onSubmitButton={handleSubmitDelete}
        />
      </Content>
    </Container>
  )
}

export default React.forwardRef(FileDetails)
